import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadsDetailsService } from '../../../services/leads-details-service.service';
import { InfoField } from '../../../components/shared/info-field/info-field';
import { OfferInfo } from '../../../components/offer-info/offer-info';
import { InfoFieldItem } from '../../../components/shared/info-field/info-field';
import { LeadData, OfertaService, OfertaResponse } from '../../../services/maia.service';
import { LocalStorageUtil } from '../../../utils/local-storage.util';
import { OauthResult, Dealership } from '../../../interfaces/oauth.interface';
import { SelectedDnService } from '../../../services/selected-dn.service';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../../services/config.service';

@Component({
  selector: 'app-leads-offers',
  standalone: true,
  imports: [OfferInfo, CommonModule, InfoField],
  templateUrl: './leads-offers.html',
  styleUrls: ['./leads-offers.scss']
})
export class LeadsOffers implements OnInit {
  private dnSubscription?: Subscription;
  private readonly invalidSimulationImageFragments = [
    'assets/img/veiculos/nova-oferta/hatch.jpg',
    'assets/img/veiculos/nova-oferta/silhueta',
    'silhueta'
  ];
  
  isLoading: boolean = true;
  errorMessage: string | null = null;
  leadId: string = '';
  cdSiglaTipo: string | null = null;
  nmCampanha: string | null = null;

  offers: any[] = [];
  selectedOffer: any = null;

  sliderValue: number = 0;

  // Estado dos envios
  isSendingAmigavel: boolean = false;
  isSendingFormal: boolean = false;
  isSendingPersuasiva: boolean = false;
  isSendingServicosFinanceiros: boolean = false;

  // Texto atual que será passado para o componente filho
  currentOfferText: string = '';

  // Estado geral de loading do texto - INICIA COMO TRUE
  isLoadingText: boolean = true;

  // Tabelas
  infoLinha1: InfoFieldItem[] = [];
  infoLinha2: InfoFieldItem[] = [];
  infoLinha3: InfoFieldItem[] = [];

  // Dados do usuário
  user: OauthResult | null = null;

  // Dados para API
  leadDataForApi: LeadData = {
    nome: '',
    nomeDoVendedor: '',
    valorParcela: '',
    modeloCompleto: '',
    versao: '',
    nomeDealer: ''
  };

  constructor(
    private route: ActivatedRoute,
    private leadsDetailsService: LeadsDetailsService,
    private ofertaService: OfertaService,
    private selectedDnService: SelectedDnService,
    private configService: ConfigService,
    private router: Router
  ) { }

  ngOnInit() {
    // Carrega dados do usuário do localStorage
    this.user = LocalStorageUtil.getVWUser();

    this.route.params.subscribe(params => {
      this.leadId = params['leadId'] || null;
      if (this.leadId) {
        this.loadLeadData();
      }
    });

    this.route.queryParams.subscribe(queryParams => {
      if (!this.leadId && queryParams['idLead']) {
        this.leadId = queryParams['idLead'];
        this.loadLeadData();
      }
    });

    // Primeiro configurar a subscription do DN
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (this.configService.isDebugEnabled()) {
        console.log('Dashboard - DN changed:', dn);
      }
      if (dn && !this.isLoading) {
        // Se já tivermos feito a carga inicial, recarregar tudo
        this.router.navigate(['/dashboard']);
      }
    });     
  }

  loadLeadData() {
    if (!this.leadId) {
      this.errorMessage = 'Lead ID não encontrado';
      this.isLoading = false;
      this.isLoadingText = false;
      return;
    }

    this.isLoading = true;
    this.isLoadingText = true;

    this.leadsDetailsService.getLeadDetails(this.leadId).subscribe({
      next: (leadData) => {
        if (this.configService.isDebugEnabled()) {
          console.log('Dados do lead:', leadData);
        }
        this.cdSiglaTipo = leadData.cdSiglaTipo || null;
        this.sliderValue = leadData.percentualEntrada || 0;
        this.nmCampanha = leadData.nmCampanha || null;

        // Preparar dados para a API
        this.prepareLeadDataForApi(leadData);

        // Enviar oferta amigável automaticamente ao carregar
        this.enviarOfertaAmigavel();

        this.loadOffers(leadData);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar dados do lead:', error);
        this.errorMessage = 'Erro ao carregar dados do lead';
        this.isLoading = false;
        this.isLoadingText = false;
      }
    });
  }

  prepareLeadDataForApi(leadData: any) {
    // Pega o nome do dealer atual do usuário logado
    const dealerName = this.getSelectedDealershipName();

    // Mapear dados para API
    this.leadDataForApi = {
      nome: leadData.nome || 'Cliente',
      nomeDoVendedor: this.user?.name || 'Vendedor',
      valorParcela: this.formatBRCoin(leadData.valorParcela || 0),
      modeloCompleto: leadData.modeloCompleto || '',
      versao: leadData.versao || '',
      nomeDealer: dealerName
    };

    if (this.configService.isDebugEnabled()) {
      console.log('Dados preparados para API:', this.leadDataForApi);
      console.log('Nome do Dealer usado:', dealerName);
    }
  }

  private getSelectedDealershipName(): string {
    try {
      const selectedDn = this.selectedDnService.getSelectedDn();
      if (this.configService.isDebugEnabled()) {
        console.log('DN selecionado:', selectedDn);
      }

      if (!this.user) {
        this.user = LocalStorageUtil.getVWUser();
      }

      if (!this.user || !this.user.montadoras) {
        if (this.configService.isDebugEnabled()) {
          console.warn('Usuário não encontrado no localStorage, usando valor padrão');
        }
        return 'VW Original Leste';
      }

      for (const montadora of this.user.montadoras) {
        for (const concessionaria of montadora.concessionarias) {
          if (String(concessionaria.codigo) === String(selectedDn)) {
            if (this.configService.isDebugEnabled()) {
              console.log('Dealership encontrado:', concessionaria.nome);
            }
            return concessionaria.nome;
          }
        }
      }

      if (this.user.montadoras.length > 0 &&
        this.user.montadoras[0].concessionarias.length > 0) {
        const primeiraConcessionaria = this.user.montadoras[0].concessionarias[0];
        if (this.configService.isDebugEnabled()) {
          console.log('Usando primeira concessionária:', primeiraConcessionaria.nome);
        }
        return primeiraConcessionaria.nome;
      }

      return 'VW Original Leste';

    } catch (error) {
      console.error('Erro ao obter nome da concessionária:', error);
      return 'VW Original Leste';
    }
  }

  onSendAmigavel() {
    this.enviarOfertaAmigavel();
  }

  onSendFormal() {
    this.enviarOfertaFormal();
  }

  onSendPersuasiva() {
    this.enviarOfertaPersuasiva();
  }

  onSendServicosFinanceiros() {
    this.enviarOfertaServicosFinanceiros();
  }

  onSendEmail(event: { email: string, text: string }) {
    this.openEmailClient(event.email, event.text);
  }

  onSendWhatsapp(event: { phone: string, text: string }) {
    this.openWhatsApp(event.phone, event.text);
  }

  enviarOfertaAmigavel() {
    this.isSendingAmigavel = true;
    this.isLoadingText = true;
    this.currentOfferText = '';

    this.ofertaService.enviarOfertaAmigavel(this.leadDataForApi, this.nmCampanha || 'Simulação Online')
      .subscribe({
        next: (resposta: OfertaResponse) => {
          if (this.configService.isDebugEnabled()) {
            console.log('✅ Oferta amigável enviada:', resposta);
          }
          this.currentOfferText = resposta.oferta;
          this.isSendingAmigavel = false;
          this.isLoadingText = false;
        },
        error: (erro) => {
          console.error('❌ Erro ao enviar oferta amigável:', erro);
          this.currentOfferText = "Erro ao gerar oferta amigável. Tente novamente.";
          this.isSendingAmigavel = false;
          this.isLoadingText = false;
        }
      });
  }

  enviarOfertaFormal() {
    this.isSendingFormal = true;
    this.isLoadingText = true;
    this.currentOfferText = '';

    this.ofertaService.enviarOfertaFormal(this.leadDataForApi, 'Simulação Online')
      .subscribe({
        next: (resposta: OfertaResponse) => {
          if (this.configService.isDebugEnabled()) {
            console.log('✅ Oferta formal enviada:', resposta);
          }
          this.currentOfferText = resposta.oferta;
          this.isSendingFormal = false;
          this.isLoadingText = false;
        },
        error: (erro) => {
          console.error('❌ Erro ao enviar oferta formal:', erro);
          this.currentOfferText = "Erro ao gerar oferta formal. Tente novamente.";
          this.isSendingFormal = false;
          this.isLoadingText = false;
        }
      });
  }

  enviarOfertaPersuasiva() {
    this.isSendingPersuasiva = true;
    this.isLoadingText = true;
    this.currentOfferText = '';

    this.ofertaService.enviarOfertaPersuasiva(this.leadDataForApi, 'Simulação Online')
      .subscribe({
        next: (resposta: OfertaResponse) => {
          if (this.configService.isDebugEnabled()) {
            console.log('✅ Oferta persuasiva enviada:', resposta);
          }
          this.currentOfferText = resposta.oferta;
          this.isSendingPersuasiva = false;
          this.isLoadingText = false;
        },
        error: (erro) => {
          console.error('❌ Erro ao enviar oferta persuasiva:', erro);
          this.currentOfferText = "Erro ao gerar oferta persuasiva. Tente novamente.";
          this.isSendingPersuasiva = false;
          this.isLoadingText = false;
        }
      });
  }

  enviarOfertaServicosFinanceiros() {
    this.isSendingServicosFinanceiros = true;
    this.isLoadingText = true;
    this.currentOfferText = '';

    this.ofertaService.enviarOfertaServicosFinanceiros(this.leadDataForApi, 'Simulação Online')
      .subscribe({
        next: (resposta: OfertaResponse) => {
          if (this.configService.isDebugEnabled()) {
            console.log('✅ Oferta de serviços financeiros enviada:', resposta);
          }
          this.currentOfferText = resposta.oferta;
          this.isSendingServicosFinanceiros = false;
          this.isLoadingText = false;
        },
        error: (erro) => {
          console.error('❌ Erro ao enviar oferta de serviços financeiros:', erro);
          this.currentOfferText = "Erro ao gerar oferta de serviços financeiros. Tente novamente.";
          this.isSendingServicosFinanceiros = false;
          this.isLoadingText = false;
        }
      });
  }

  private openEmailClient(email: string, text: string) {
    const encodedText = encodeURIComponent(text);
    const encodedSubject = encodeURIComponent('Oferta Especial - ' + (this.selectedOffer?.title || 'Veículo'));

    this.showEmailOptionsModal(email, encodedSubject, encodedText);
  }

  private showEmailOptionsModal(email: string, subject: string, body: string) {
    // Remover modal existente
    const existingModal = document.getElementById('emailOptionsModal');
    if (existingModal) {
      existingModal.remove();
    }

    // Criar modal simples
    const modal = document.createElement('div');
    modal.id = 'emailOptionsModal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
      max-width: 400px;
      width: 90%;
    `;

    modalContent.innerHTML = `
      <h3 style="margin-top: 0; color: #001E50;">Enviar Oferta por Email</h3>
      <p style="color: #001E50; margin-bottom: 20px;">Escolha como deseja enviar a oferta:</p>

      <div style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px;">

        <button id="outlookWebBtn" style="
          padding: 12px;
          background: #001E50;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        ">
          🌐 Outlook Web
        </button>

        <button id="defaultEmailBtn" style="
          padding: 12px;
          background: #001E50;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        ">
          📨 Email Padrão do Sistema
        </button>
      </div>

      <button id="cancelBtn" style="
        padding: 8px 16px;
        background: transparent;
        color: #666;
        border: 1px solid #ddd;
        border-radius: 4px;
        cursor: pointer;
        float: right;
      ">
        Cancelar
      </button>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Event listeners - CORRIGIDO
    document.getElementById('outlookWebBtn')?.addEventListener('click', () => {
      const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?to=${email}&subject=${subject}&body=${body}`;
      window.open(outlookWebUrl, '_blank');
      modal.remove();
    });

    document.getElementById('outlookDesktopBtn')?.addEventListener('click', () => {
      this.openOutlookDesktop(email, subject, body);
      modal.remove();
    });

    document.getElementById('defaultEmailBtn')?.addEventListener('click', () => {
      const mailtoUrl = `mailto:${email}?subject=${subject}&body=${body}`;
      window.location.href = mailtoUrl;
      modal.remove();
    });

    document.getElementById('cancelBtn')?.addEventListener('click', () => {
      modal.remove();
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  private openOutlookDesktop(email: string, subject: string, body: string) {
    // SOLUÇÃO CORRETA: Usar diferentes métodos para diferentes navegadores

    const outlookUrl = `outlook:compose?to=${email}&subject=${subject}&body=${body}`;

    // Verificar se é Microsoft Edge/IE
    const isEdge = navigator.userAgent.includes('Edg') ||
      navigator.userAgent.includes('MSIE') ||
      navigator.userAgent.includes('Trident');

    if (isEdge) {
      // Para Edge/IE, usar window.location.href
      if (this.configService.isDebugEnabled()) {
        console.log('Usando método Edge/IE');
      }
      window.location.href = outlookUrl;
    } else {
      // Para Chrome/Firefox/Outros, usar iframe
      if (this.configService.isDebugEnabled()) {
        console.log('Usando método iframe para Chrome/Firefox');
      }
      this.openOutlookViaIframe(outlookUrl);
    }

    // Fallback após 1.5 segundos
    setTimeout(() => {
      // Verificar se não saiu da página (não abriu o Outlook)
      if (document.visibilityState === 'visible') {
        if (this.configService.isDebugEnabled()) {
          console.log('Fallback acionado - Outlook não abriu');
        }
        this.showOutlookFallback(email, subject, body);
      }
    }, 1500);
  }

  private openOutlookViaIframe(url: string) {
    try {
      // Método mais confiável para navegadores modernos
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.position = 'fixed';
      iframe.style.left = '-1000px';
      iframe.style.top = '-1000px';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;

      document.body.appendChild(iframe);

      // Remover após alguns segundos
      setTimeout(() => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      }, 3000);

    } catch (error) {
      console.error('Erro ao abrir via iframe:', error);
      // Fallback para método tradicional
      window.open(url, '_blank');
    }
  }

  private showOutlookFallback(email: string, subject: string, body: string) {
    if (confirm('Não foi possível abrir o Outlook Desktop. Possíveis causas:\n\n' +
      '1. O Outlook não está instalado\n' +
      '2. O navegador está bloqueando\n\n' +
      'Deseja abrir o Outlook Web?')) {
      const outlookWebUrl = `https://outlook.office.com/mail/deeplink/compose?to=${email}&subject=${subject}&body=${body}`;
      window.open(outlookWebUrl, '_blank');
    }
  }

  private openWhatsApp(phone: string, text: string) {
    const encodedText = encodeURIComponent(text);
    let formattedNumber = phone.replace(/\D/g, '');

    if (!formattedNumber.startsWith('55')) {
      formattedNumber = '55' + formattedNumber;
    }

    // Usar template do environment
    const whatsappUrl = this.configService.getRequiredByPath<string>('external.whatsappUrl')
      .replace('{phone}', formattedNumber)
      .replace('{text}', encodedText);

    window.open(whatsappUrl, '_blank');
  }

  loadOffers(leadData: any) {

    this.leadsDetailsService.getSimulacaoDetails(
      leadData.codigoSimulacao
    ).subscribe({
      next: (response) => {
        const carCardCarImage = this.buildSimulationImageUrl(response?.ImagemVeiculo);

        if (this.offers?.length) {
          this.offers[0].imageUrl = carCardCarImage;
          this.offers = [...this.offers];
        }
      },
      error: () => {
        if (this.offers?.length) {
          this.offers[0].imageUrl = '';
          this.offers = [...this.offers];
        }
      }
    });

    this.offers = [
      {
        title: leadData.modeloCompleto,
        version: leadData.versao || '',
        entrada: leadData.valorEntrada ? this.formatBRCoin(leadData.valorEntrada) : 'R$ 0,00',
        preco: leadData.valorPreco ? this.formatBRCoin(leadData.valorPreco) : 'R$ 0,00',
        imageUrl: '',
        parcelas: leadData.valorParcela || 2500,
        anoModelo: leadData.anoModelo?.toString() || '-',
        contactMail: leadData.email,
        contactCelphone: leadData.celular
      }
    ];
    if (this.configService.isDebugEnabled()) {
      console.log('Ofertas carregadas:', this.offers);
    }

    this.offers = this.offers.map(offer => ({
      ...offer,
      carInfos: [
        { title: 'Ano Modelo', value: offer.anoModelo },
        { title: 'Valor do Veículo', value: offer.preco },
        { title: 'Valor da Entrada', value: offer.entrada }
      ]
    }));

    if (this.offers.length > 0) {
      this.selectedOffer = this.offers[0];
      this.updateTablesData(leadData);
    }
  }

  updateTablesData(leadData: any) {
    if (this.getLayoutType() !== 'PA' && this.getLayoutType() !== 'PR') {
      this.infoLinha1 = [
        {
          type: 'text',
          title: 'Estado do Veículo',
          info: leadData.condicaoVeiculo || 'Novo'
        },
        {
          type: 'text',
          title: 'Ano Fabricação',
          info: leadData.anoFabricacao?.toString() || '-'
        },
        {
          type: 'text',
          title: 'Ano Modelo',
          info: leadData.anoModelo?.toString() || '-'
        }
      ];

      const valorVeiculo = leadData.valorVeiculo || 0;
      const valorEntrada = leadData.valorEntrada || 0;
      const valorFinanciado = leadData.valorFinanciado || 0;

      this.infoLinha2 = [
        {
          type: 'text',
          title: 'Valor do Veículo',
          info: this.formatBRCoin(valorVeiculo)
        },
        {
          type: 'text',
          title: 'Valor de Entrada',
          info: this.formatBRCoin(valorEntrada)
        },
        {
          type: 'text',
          title: 'Entrada (%)',
          info: `${this.sliderValue}%`
        },
        {
          type: 'text',
          title: 'Valor Financiado',
          info: this.formatBRCoin(valorFinanciado)
        }
      ];

      this.infoLinha3 = [
        {
          type: 'text',
          title: 'Número de Parcelas',
          info: leadData.quantidadeParcelas ? `${leadData.quantidadeParcelas}x` : '-'
        },
        {
          type: 'text',
          title: 'Carência (dias)',
          info: leadData.carenciaDias?.toString() || '0'
        },
        {
          type: 'text',
          title: 'Tipo de Pessoa',
          info: leadData.tipoPessoa || 'Física'
        },
        {
          type: 'text',
          title: 'Segmento',
          info: leadData.tipoCompra || 'Particular'
        }
      ];
    }
  }

  getLayoutType(): string | null {
    if (!this.cdSiglaTipo) return null;
    const tipo = this.cdSiglaTipo.toUpperCase();
    if (tipo === 'PA') return 'PA';
    if (tipo === 'PR' || tipo.startsWith('CAPR')) return 'PR';
    return 'SO';
  }

  formatBRCoin(valor: number): string {
    if (!valor) return 'R$ 0,00';
    return 'R$ ' + valor.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  navigateToPage(page: number) {
    if (this.leadId) {
      // Usar rota com leadId
      const routesWithId = [
        `/leads/${this.leadId}/detalhes`,
        `/leads/${this.leadId}/leads-offers`,
        `/leads/${this.leadId}/leads-history`
      ];
      this.router.navigate([routesWithId[page]]);
    } else {
      // Fallback para rotas antigas
      const pageRoutes = ['/leads/leads-info', '/leads/leads-offers', '/leads/leads-history'];
      this.router.navigate([pageRoutes[page]]);
    }
  }

  private buildSimulationImageUrl(imagePath: string | null | undefined): string {
    if (!imagePath) {
      return '';
    }

    const normalizedImagePath = String(imagePath).trim();
    if (!normalizedImagePath) {
      return '';
    }

    const imageUrl = this.isAbsoluteUrl(normalizedImagePath)
      ? normalizedImagePath
      : `${this.configService.getRequiredByPath<string>('bancoVw.imageBaseUrl')}/${normalizedImagePath.replace(/^\/+/, '')}`;

    return this.isValidSimulationImage(imageUrl) ? imageUrl : '';
  }

  private isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
  }

  private isValidSimulationImage(imageUrl: string): boolean {
    const normalizedUrl = imageUrl.trim().toLowerCase();

    if (!normalizedUrl) {
      return false;
    }

    return !this.invalidSimulationImageFragments.some(fragment => normalizedUrl.includes(fragment));
  }
}

